// Typed wrappers over the sync RPCs (WEB_PLAN.md §0 "Transport", §3.3, §3.6, §3.11). Only sync/ calls these.
// Parsing is tolerant like SupabaseSyncAPI (GroundTeam/Sync/Supabase/SupabaseSyncAPI.swift): missing or null
// optional fields fall back to defaults and rows naming an unknown collection are skipped, so a newer server never
// breaks an older console.
//
// Everything is injectable: pull.ts / push.ts / engine.ts depend on the `SyncApi` interface only, and the Supabase
// implementation takes the client as a parameter (this module never imports '@/supabase', so tests can load it).

import type { SupabaseClient } from '@supabase/supabase-js';
import { isCollection, type Collection } from '@/contracts/collections';
import type { JsonObject, PullResult, PushError, PushItem, PushResult, PushSaved, Row, ServerErrorKind } from '@/contracts/wire';
import { isJsonObject } from './canonical';

/**
 * Wake teammates' phones after a save (§3.11). The Edge Functions answer browsers' CORS preflights for this site
 * (supabase/functions/_shared/http.ts, Appendix A item 1), and the call is safe from a browser: it needs the user's
 * JWT and an active membership, the engine sends at most one call per 20 s (NOTIFY_THROTTLE_MS), the server adds a
 * best-effort throttle of one fan-out per company per 15 s (an in-memory map per function instance, so parallel or
 * freshly started instances don't share it), and it only sends a silent push naming the company. Until the functions
 * are redeployed with CORS the call fails without an answer, which notifyDevices ignores (phones catch up on their
 * poll), but the browser console logs a CORS error on every save: deploy the functions before a web build with this on.
 */
export const NOTIFY_DEVICES_FROM_WEB = true;

/** `sync_push` refuses more than this many items per call (M1:1098). The engine sends ≤ 200 anyway. */
export const SERVER_MAX_PUSH_ITEMS = 500;

export type SyncErrorKind = ServerErrorKind | 'network' | 'auth' | 'unknown';

const SERVER_ERROR_KINDS: readonly ServerErrorKind[] = [
  'not_member',
  'suspended',
  'forbidden',
  'last_admin',
  'invalid_input',
  'already_member',
  'no_invitation',
  'invalid_code',
  'invitation_expired',
  'too_many_invitations',
  'company_creation_disabled',
];

function asServerKind(value: unknown): ServerErrorKind | null {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  return (SERVER_ERROR_KINDS as readonly string[]).includes(v) ? (v as ServerErrorKind) : null;
}

/** An RPC failure, classified the way SupabaseError.serverKind / isRetryable / isAuthFailure do on iOS. */
export class SyncApiError extends Error {
  readonly kind: SyncErrorKind;
  /** HTTP status (0 when the request never got an answer). */
  readonly status: number;
  /** PostgREST SQLSTATE or error code, when there was one. */
  readonly code: string | null;

  constructor(kind: SyncErrorKind, message: string, status = 0, code: string | null = null) {
    super(message);
    this.name = 'SyncApiError';
    this.kind = kind;
    this.status = status;
    this.code = code;
  }

  /** Offline, timeouts, 5xx, 429: keep the work and try again later. */
  get retryable(): boolean {
    return this.kind === 'network' || this.status >= 500 || this.status === 429;
  }

  /** The server says this person is no longer (actively) in the company: stop syncing. */
  get isRemoval(): boolean {
    return this.kind === 'not_member' || this.kind === 'suspended';
  }
}

export function isSyncApiError(error: unknown): error is SyncApiError {
  return error instanceof SyncApiError;
}

/** What people read for a sync failure (wording of SupabaseError.errorDescription). */
export function describeSyncError(error: unknown): string {
  if (!(error instanceof SyncApiError)) {
    return error instanceof Error && error.message ? error.message : 'Something went wrong while syncing.';
  }
  switch (error.kind) {
    case 'network':
      return "You're offline or the server can't be reached. Changes will sync when the connection is back.";
    case 'auth':
      return 'Your session expired. Please sign in again.';
    case 'not_member':
      return "You're no longer a member of this company.";
    case 'suspended':
      return 'Your account has been suspended by an admin.';
    case 'forbidden':
      return "You don't have permission to do that.";
    case 'last_admin':
      return 'The company must keep at least one active admin.';
    case 'invalid_input':
      return "Some of the information entered isn't valid.";
    default:
      if (error.status === 429) return 'Too many requests. Please wait a moment and try again.';
      return error.message.trim() || `Server error (${error.status}).`;
  }
}

/**
 * Maps a supabase-js / PostgREST error to a SyncApiError. The RPCs raise their conditions as the exception message
 * (`raise exception using errcode = '42501', message = 'not_member'`), which PostgREST returns in `message`
 * (HTTP 403 for an authenticated caller). A 401 is a session problem (expired/revoked JWT, or no session at all).
 */
export function mapRpcError(error: { message?: unknown; code?: unknown } | null | undefined, status: number): SyncApiError {
  const message = typeof error?.message === 'string' ? error.message : '';
  const code = typeof error?.code === 'string' && error.code ? error.code : null;
  if (status === 0) return new SyncApiError('network', message || 'Network request failed', 0, code);
  if (status === 401) return new SyncApiError('auth', message || 'Unauthorized', status, code);
  const kind = asServerKind(message) ?? asServerKind(code);
  if (kind) return new SyncApiError(kind, message.trim() || kind, status, code);
  return new SyncApiError('unknown', message || `HTTP ${status}`, status, code);
}

// MARK: - Tolerant parsing (SupabaseSyncAPI.row / int64 / bool / string)

function int(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  }
  return null;
}

function bool(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.toLowerCase();
    if (v === 'true' || v === 't' || v === '1' || v === 'yes') return true;
    if (v === 'false' || v === 'f' || v === '0' || v === 'no') return false;
  }
  return null;
}

function str(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function name(d: Record<string, unknown>): { collection: Collection; id: string } | null {
  const collection = d.collection;
  const id = typeof d.id === 'string' ? d.id.trim() : null;
  if (!isCollection(collection) || !id || !UUID_RE.test(id)) return null;
  return { collection, id };
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function array(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** One `row_json` (M1:369). Null when the collection is unknown or the id is not a UUID. */
export function parseRow(value: unknown): Row | null {
  const d = record(value);
  if (!d) return null;
  const n = name(d);
  if (!n) return null;
  const deleted = bool(d.deleted) ?? false;
  let payload: JsonObject | null = null;
  if (!deleted) {
    let object: unknown = d.payload;
    if (typeof object === 'string') {
      try {
        object = JSON.parse(object); // payload sent as JSON text
      } catch {
        object = null;
      }
    }
    payload = isJsonObject(object) ? (object as JsonObject) : null;
  }
  const filePath = str(d.file_path);
  return {
    collection: n.collection,
    id: n.id,
    payload,
    modified_at: str(d.modified_at) ?? '',
    modified_by: str(d.modified_by) ?? '',
    version: int(d.version) ?? 0,
    server_seq: int(d.server_seq) ?? 0,
    deleted,
    file_path: filePath && !deleted ? filePath : null,
  };
}

export function parsePullResult(value: unknown, after: number): PullResult {
  const obj = record(value);
  if (!obj) throw new SyncApiError('unknown', 'Unexpected response from the server (sync_pull).');
  const items: Row[] = [];
  for (const entry of array(obj.items)) {
    const row = parseRow(entry);
    if (row) items.push(row);
  }
  const revoked: { collection: Collection; id: string }[] = [];
  for (const entry of array(obj.revoked)) {
    const d = record(entry);
    const n = d && name(d);
    if (n) revoked.push(n);
  }
  return {
    items,
    revoked,
    next: int(obj.next) ?? after,
    has_more: bool(obj.has_more) ?? false,
    epoch: int(obj.epoch) ?? 1,
  };
}

export function parsePushResult(value: unknown): PushResult {
  const obj = record(value);
  if (!obj) throw new SyncApiError('unknown', 'Unexpected response from the server (sync_push).');
  const saved: PushSaved[] = [];
  for (const entry of array(obj.saved)) {
    const d = record(entry);
    const n = d && name(d);
    if (!d || !n) continue;
    saved.push({ ...n, version: int(d.version) ?? 0, server_seq: int(d.server_seq) ?? 0, deleted: bool(d.deleted) ?? false });
  }
  const conflicts: Row[] = [];
  for (const entry of array(obj.conflicts)) {
    const row = parseRow(entry);
    if (row) conflicts.push(row);
  }
  const errors: PushError[] = [];
  for (const entry of array(obj.errors)) {
    const d = record(entry);
    if (!d) continue;
    errors.push({
      collection: isCollection(d.collection) ? d.collection : null,
      id: str(d.id) ?? '',
      code: str(d.code) ?? 'invalid',
      message: str(d.message) ?? '',
    });
  }
  return { saved, conflicts, errors };
}

// MARK: - The API the engine uses

/** What pull.ts / push.ts / engine.ts need from the server. `company` is the lower-case company id. */
export interface SyncApi {
  pull(company: string, after: number, limit: number, full: boolean): Promise<PullResult>;
  push(company: string, items: PushItem[]): Promise<PushResult>;
  /** The current row if it exists and the caller may read it, else null. */
  get(company: string, collection: Collection, id: string): Promise<Row | null>;
  /** Silent push to teammates' phones after a save (no-op while disabled, §3.11). Never throws. */
  notifyDevices(company: string): Promise<void>;
}

/**
 * One RPC call with the §4.6 rule: an HTTP 401 triggers one `auth.refreshSession()` and one retry; a second 401 (or
 * a failed refresh) surfaces as kind 'auth'.
 */
async function rpc(client: SupabaseClient, fn: string, params: Record<string, unknown>): Promise<unknown> {
  let refreshed = false;
  for (;;) {
    let response: { data: unknown; error: { message?: unknown; code?: unknown } | null; status: number };
    try {
      response = (await client.rpc(fn, params)) as typeof response;
    } catch (error) {
      // supabase-js resolves transport failures as status 0, but a thrown abort or a custom fetch can reject.
      throw new SyncApiError('network', error instanceof Error ? error.message : String(error));
    }
    const { data, error, status } = response;
    if (!error) return data;
    if (status === 401 && !refreshed) {
      refreshed = true;
      const { error: refreshError } = await client.auth.refreshSession();
      if (!refreshError) continue;
    }
    throw mapRpcError(error, status);
  }
}

/** `sync_pull(p_company, p_after, p_limit, p_full)` (M1:1213). */
export async function syncPull(client: SupabaseClient, company: string, after: number, limit: number, full: boolean): Promise<PullResult> {
  const data = await rpc(client, 'sync_pull', { p_company: company, p_after: after, p_limit: limit, p_full: full });
  return parsePullResult(data, after);
}

/** `sync_push(p_company, p_items)` (M1:1068), in chunks of at most 500 like SupabaseSyncAPI.push. */
export async function syncPush(client: SupabaseClient, company: string, items: PushItem[]): Promise<PushResult> {
  const result: PushResult = { saved: [], conflicts: [], errors: [] };
  for (let start = 0; start < items.length; start += SERVER_MAX_PUSH_ITEMS) {
    const chunk = items.slice(start, start + SERVER_MAX_PUSH_ITEMS);
    const data = await rpc(client, 'sync_push', { p_company: company, p_items: chunk });
    const part = parsePushResult(data);
    result.saved.push(...part.saved);
    result.conflicts.push(...part.conflicts);
    result.errors.push(...part.errors);
  }
  return result;
}

/** `sync_get(p_company, p_collection, p_id)` (M1:1265): null when missing or not readable. */
export async function syncGet(client: SupabaseClient, company: string, collection: Collection, id: string): Promise<Row | null> {
  const data = await rpc(client, 'sync_get', { p_company: company, p_collection: collection, p_id: id });
  if (data === null || data === undefined) return null;
  const row = parseRow(data);
  if (!row && record(data)) throw new SyncApiError('unknown', 'Unexpected response from the server (sync_get row).');
  return row;
}

/** `POST /functions/v1/notify-devices {"company_id": "<lowercase>"}` (iOS notifyTeammates). Best effort. */
export async function notifyDevices(client: SupabaseClient, company: string, enabled: boolean = NOTIFY_DEVICES_FROM_WEB): Promise<void> {
  if (!enabled) return;
  try {
    await client.functions.invoke('notify-devices', { body: { company_id: company.toLowerCase() } });
  } catch {
    // Phones still catch up on their poll.
  }
}

export function createSupabaseSyncApi(client: SupabaseClient, options: { notifyDevices?: boolean } = {}): SyncApi {
  const notify = options.notifyDevices ?? NOTIFY_DEVICES_FROM_WEB;
  return {
    pull: (company, after, limit, full) => syncPull(client, company, after, limit, full),
    push: (company, items) => syncPush(client, company, items),
    get: (company, collection, id) => syncGet(client, company, collection, id),
    notifyDevices: (company) => notifyDevices(client, company, notify),
  };
}
